import {
  Modal,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { documentBuilderService } from "../../services/documentBuilderService";
import Loader from "../../components/Loader";

export interface FileManagerModalProps {
  open: boolean;
  onClose: () => void;
  currentFolderName: string;
  currentFolderContent: any[];
  onFileSelect: (file: any) => void;
}

export default function FileManagerModal({
  open,
  onClose,
  currentFolderName,
  currentFolderContent,
  onFileSelect,
}: FileManagerModalProps) {
  const theme = useTheme();

  // Tree structure: each folder has children
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loadingFolders, setLoadingFolders] = useState<Record<string, boolean>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  // Initialize root folder
  useEffect(() => {
  if (open) {
    setLoading(true); // start loading
    (async () => {
      try {
        // If currentFolderContent is empty, fetch root folder from API
        let rootContent = currentFolderContent;
        if (!rootContent || rootContent.length === 0) {
          rootContent = await documentBuilderService.listDocuments("/");
        }

        setFolderTree(rootContent || []);
        setExpanded({});
        setLoadingFolders({});
      } catch (err) {
        console.error("Failed to load root folder", err);
      } finally {
        setLoading(false); // stop loading
      }
    })();
  }
}, [open, currentFolderContent]);

  const toggleFolder = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFolderClick = async (folder: any, parentPath: string) => {
    toggleFolder(folder.id);

    // If folder already has children, no need to fetch again
    if (folder.children) return;

    const folderPath =
      parentPath === "/" ? `/${folder.title}` : `${parentPath}/${folder.title}`;
    setLoadingFolders((prev) => ({ ...prev, [folder.id]: true }));

    try {
      const files = await documentBuilderService.listDocuments(folderPath);
      // Save children inside the folder
      folder.children = files;
      setFolderTree([...folderTree]); // trigger re-render
    } catch (err) {
      console.error("Failed to load folder contents", err);
    } finally {
      setLoadingFolders((prev) => ({ ...prev, [folder.id]: false }));
    }
  };

  const renderItems = (items: any[], level = 0, parentPath = "/") =>
    items.map((item) => {
      const isFolder = item.type === "folder";
      const isOpen = expanded[item.id];

      return (
        <Box key={item.id}>
          <ListItemButton
            sx={{
              pl: 2 + level * 2,
              borderRadius: 1,
              mb: 0.5,
              color: theme.palette.text.primary,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
            onClick={() => {
              if (isFolder) {
                handleFolderClick(item, parentPath);
              } else {
                onFileSelect(item);
                onClose();
              }
            }}
          >
            {isFolder && (
              <ChevronRightIcon
                sx={{
                  fontSize: 18,
                  mr: 0.5,
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "0.2s",
                  color: theme.palette.text.secondary,
                }}
              />
            )}

            <ListItemIcon
              sx={{ minWidth: 32, color: theme.palette.text.secondary }}
            >
              {isFolder ? (
                <FolderIcon fontSize="small" />
              ) : (
                <DescriptionIcon fontSize="small" />
              )}
            </ListItemIcon>

            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: 14,
                color: theme.palette.text.primary,
              }}
            />
          </ListItemButton>

          {isFolder && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {loadingFolders[item.id] ? (
                <Box
                  sx={{ ml: 4, py: 1, display: "flex", alignItems: "center" }}
                >
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : item.children ? (
                renderItems(
                  item.children,
                  level + 1,
                  parentPath === "/"
                    ? `/${item.title}`
                    : `${parentPath}/${item.title}`,
                )
              ) : null}
            </Collapse>
          )}
        </Box>
      );
    });

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      hideBackdrop
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: 340,
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          p: 3,
          overflowY: "auto",
          boxShadow: theme.shadows[5],
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: theme.palette.text.primary,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            ml: 1,
            color: theme.palette.text.primary,
          }}
        >
          File Manager
        </Typography>

    {loading ? (
    <Box>
      <Loader/>
      
    </Box>
  ) : (
    <List dense>{renderItems(folderTree)}</List>
  )}
      </Box>
    </Modal>
  );
}
